import moment from 'moment-timezone';
import {
  TIME_FORMAT,
  DATE_FORMAT_SHORT,
  DATE_FORMAT_NO_TIME_ZONE,
  CSV_CONTACT_LIMIT,
} from 'constants/config';
import { Buffer } from 'buffer';
import iconv from 'iconv-lite';
import jschardet from 'jschardet';
import { isEmpty } from 'lodash';

export const roundDown = (num) => {
  if (num) {
    return Math.floor(num);
  }
  return 0;
};

export const addSuffix = n => (['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th');

export const resultsToOptions = (results, valueName, idName = 'id') => {
  if (results && results.length > 0) {
    return results.map(result => ({
      label: valueName instanceof Function ? valueName(result) : result[valueName],
      value: result[idName],
    }));
  }
  return [];
};

export const stepsToOptions = (results, idName = 'id') => {
  if (results && results.length > 0) {
    return results.map((result, i) => {
      const ii = i + 1;
      return { label: ii + addSuffix(ii), value: result[idName] };
    });
  }
  return [];
};

// eslint-disable-next-line
export const uuidv4 = () => {
  // eslint-disable-next-line
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    // eslint-disable-next-line
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16));
};

export const truncateText = (str, length = 100, ending = '...') => {
  if (str.length > length) {
    return `${str.substring(0, length - ending.length)}${ending}`;
  }

  return str;
};


export const dataURLtoFile = (dataurl, filename) => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n) {
    u8arr[n - 1] = bstr.charCodeAt(n - 1);
    n -= 1;
  }
  return new File([u8arr], filename, { type: mime });
};

export const flattenKeyPairArray = (array, key) => {
  if (array.length > 0) {
    return array.map(l => (l[key]));
  }
  return array;
};

export const emailToDomain = email => email.replace(/.*@/, '');

export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export const getLetterInAlphabet = index => 'abcdefghijklmnopqrstuvwxyz'.split('')[index];

export const secondsToDays = seconds => (seconds > 0 ? seconds / 86400 : 0);

export const redirectApp = (url, params) => {
  const urlEncodedParams = new URLSearchParams(params);
  window.location.replace(`${url}?${urlEncodedParams}`);
};

export const openNewTab = (url, params) => {
  const urlEncodedParams = new URLSearchParams(params);
  window.open(`${url}?${urlEncodedParams}`);
};

export const calculatePercentage = (total, value) => {
  if (total && total !== 0) {
    return ((value / total) * 100).toFixed(1);
  }
  return 0;
};

export const sameDay = (d1, d2) => (
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate()
);

export const removeTZ = date => (date.format(DATE_FORMAT_NO_TIME_ZONE));

// eslint-disable-next-line no-confusing-arrow
export const formatInboxTimestamp = timestamp => sameDay(new Date(), timestamp) ?
  moment(timestamp).format(TIME_FORMAT) : moment(timestamp).format(DATE_FORMAT_SHORT);

export const formatStat = (total, value) => `${calculatePercentage(total, value)}% (${value})`;

// get delta minutes between local timezone and an specific timezone
export const getDiffInMinutes = (timezone) => {
  const now = moment();
  const localOffset = now.utcOffset();
  now.tz(timezone);
  const centralOffset = now.utcOffset();
  return localOffset - centralOffset;
};

export const CSVToArray = (strData, strDelimiter = ',', limitRows = CSV_CONTACT_LIMIT) => {
  // Create a regular expression to parse the CSV values.
  const objPattern = new RegExp(
    (
      `(\\${strDelimiter}|\\r?\\n|\\r|^)` + // Delimiters.
      '(?:"([^"]*(?:""[^"]*)*)"|' + // Quoted fields.
      `([^"\\${strDelimiter}\\r\\n]*))` // Standard fields.
    ), 'gi',
  );

  // Create an array to hold our data. Give the array
  // a default empty first row.
  const arrData = [[]];

  // Create an array to hold our individual pattern
  // matching groups.
  let arrMatches = null;

  // Keep looping over the regular expression matches
  // until we can no longer find a match.
  arrMatches = objPattern.exec(strData);
  while (arrMatches && limitRows >= arrData.length) {
    // Get the delimiter that was found.
    const strMatchedDelimiter = arrMatches[1];

    // Check to see if the given delimiter has a length
    // (is not the start of string) and if it matches
    // field delimiter. If id does not, then we know
    // that this delimiter is a row delimiter.
    if (
      strMatchedDelimiter.length &&
      strMatchedDelimiter !== strDelimiter
    ) {
      // Since we have reached a new row of data,
      // add an empty row to our data array.
      arrData.push([]);
    }
    let strMatchedValue;

    // Now that we have our delimiter out of the way,
    // let's check to see which kind of value we
    // captured (quoted or unquoted).
    if (arrMatches[2]) {
      // We found a quoted value. When we capture
      // this value, unescape any double quotes.
      strMatchedValue = arrMatches[2].replace(
        new RegExp('""', 'g'),
        '"',
      );
    } else {
      // We found a non-quoted value.
      /* eslint-disable prefer-destructuring */
      strMatchedValue = arrMatches[3];
    }

    // Now that we have our value string, let's add
    // it to the data array.
    arrData[arrData.length - 1].push(strMatchedValue);

    arrMatches = objPattern.exec(strData);
  }
  // Return the parsed data.
  return arrData;
};

export const getCSVDelimiter = (strData) => {
  let parsedData;
  const delimiters = [',', ';', '|', '\t'];
  const expectedFieldCount = 3; // Required fields count
  for (let i = 0; i < delimiters.length;) {
    parsedData = CSVToArray(strData, delimiters[i], 1);
    for (let j = 0; j < parsedData.length;) {
      if (parsedData[j].length > expectedFieldCount) {
        return delimiters[i];
      }
      j += 1;
    }
    i += 1;
  }
  return ',';
};

export const csvReader = (onloadCallback, onerrorCallback, readAsText) => {
  const reader = new FileReader();
  reader.onerror = onerrorCallback;
  reader.onload = (e) => {
    const result = e.target.result;
    const { encoding } = jschardet.detect(result.split(/\r|\n|\r\n/).toString());
    if (encoding !== 'UTF-8') {
      try {
        const data = iconv.decode(Buffer.from(result.trim()), encoding);
        onloadCallback(CSVToArray(data, getCSVDelimiter(data)));
      } catch (error) {
        const data = decodeURIComponent(escape(result.toString()));
        onloadCallback(CSVToArray(data, getCSVDelimiter(data)));
      }
    } else {
      const data = decodeURIComponent(escape(result.toString()));
      onloadCallback(CSVToArray(data, getCSVDelimiter(data)));
    }
  };
  reader.readAsBinaryString(readAsText);
};

export const getFullname = user => `${user.first_name} ${user.last_name}`;

export const getIdFromURL = (url, backIndex) => url.split('/').slice(-backIndex)[0];

export const getShortenedText = (text, size = 20) => (
  text.length > size ? `${text.substring(0, size - 3)}...` : text
);

export const getErrorMessage = (error) => {
  let notificationMessage;
  if (error.errors[0].non_field_errors !== undefined) {
    notificationMessage = error.errors[0].non_field_errors;
  } else {
    // eslint-disable-next-line prefer-destructuring
    notificationMessage = error.errors[0];
  }
  return notificationMessage;
};

export const listToStr = (dataList, separator = ':') => (
  !isEmpty(dataList) ? `${separator}${dataList.join(separator)}${separator}` : null
);

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / (k ** i)).toFixed(dm))} ${sizes[i]}`;
};

export const calculateOptimalEmailSize = (unit) => {
  const optimalSize = {
    Bytes: 1048576,
    KB: 1024,
    MB: 1,
    GB: 1048576,
  };
  return optimalSize[unit];
};

export const stripDecimalsAndFormat = (number, defaultValue = '') => {
  if (number === null || number === undefined) return defaultValue;
  const newNumber = parseInt(number, 10);
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 0,
  });

  return formatter.format(newNumber);
};

export const downloadBlob = (blob, filename) => {
  // Create a download link and click it programmatically
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


export const decodeHtmlEntities = (str) => {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
};

// Recursively remove empty nodes
export const removeEmptyNodes = (element) => {
  // eslint-disable-next-line no-plusplus
  for (let i = element.childNodes.length - 1; i >= 0; i--) {
    const child = element.childNodes[i];
    removeEmptyNodes(child); // recursive call for child nodes

    // Check if node is empty
    if (
      (child.nodeType === Node.ELEMENT_NODE && !child.textContent.trim()) ||
            (child.nodeType === Node.TEXT_NODE && !child.nodeValue.trim())
    ) {
      element.removeChild(child);
    }
  }
};


export const canonicalizeHtml = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  function orderAttributes(node) {
    if (node.attributes && node.attributes.length > 1) {
      const attrs = Array.from(node.attributes).sort((a, b) => a.name.localeCompare(b.name));
      attrs.forEach((attr) => {
        node.removeAttribute(attr.name);
      });
      attrs.forEach((attr) => {
        node.setAttribute(attr.name, attr.value);
      });
    }

    // Recursively order attributes for children
    node.childNodes.forEach(orderAttributes);
  }

  orderAttributes(doc.body);
  removeEmptyNodes(doc.body);

  return doc.body.innerHTML;
};


export const normalizeString = (str = '') => {
  if (!str) return str;
  const canonicalizedHtml = canonicalizeHtml(str);
  // Replace newline characters and multiple spaces with a single space
  const decodedStr = decodeHtmlEntities(canonicalizedHtml);
  return decodedStr.replace(/\s+/g, '').trim().replaceAll('\n', '').replaceAll('&nbsp;', '')
    .replaceAll('<br>', '');
};

export const getFiltersFromLocation = (location) => {
  let filters = {};
  if (!location?.search) return filters;
  const searchParams = new URLSearchParams(location?.search);
  try {
    filters = JSON.parse(searchParams?.get('filters')) || {};
  } catch (error) {
    redirectApp('/404');
  }

  return filters;
};

export const setFiltersToLocation = (location, history, data) => {
  const filterParams = {};
  Object.entries(data).forEach(([key, value]) => {
    if (!isEmpty(value)) filterParams[key] = value;
  });
  const params = new URLSearchParams();
  const serializedObject = JSON.stringify(filterParams);
  params.append('filters', serializedObject);
  history.push({
    pathname: location?.pathname,
    search: params.toString(),
  });
};
